import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  SxProps,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { AppWrapper, GridView, ShippingToSelector, Subtitle, Title, UndoSnackbar } from '../../components/_mui';
import {
  ADD_ITEM_TO_CONTINUE_TEXT,
  BACK_ARIA_LABEL,
  BACK_TO_TOP_LABEL,
  BROWSE_MARKETPLACE_LABEL,
  CLEARED_MARKETPLACE_FILTERS,
  CLEARED_MARKETPLACE_FILTER_VALUES,
  CONTINUE_TO_SHIPPING_LABEL,
  getBudgetIntroText,
  getItemsAddedText,
  getPicksReplacedText,
  LOAD_MORE_FAILED_TEXT,
  MARKETPLACE_GRID_COLUMNS,
  MARKETPLACE_GRID_GAP,
  MARKETPLACE_INFINITE_SCROLL_THRESHOLD,
  MARKETPLACE_SHIPPING_INTRO_LEAD,
  MARKETPLACE_SHIPPING_INTRO_TAIL,
  MARKETPLACE_TOTAL_CREDITS,
  RETRY_LABEL,
} from '../../constants/marketplace';
import { ROUTE_NAMES } from '../../constants/routes';
import { LOCKED_OPACITY } from '../../constants/ui';
import { useFab } from '../../hooks/useFab';
import { useFooter } from '../../hooks/useFooter';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import useWindowSize from '../../hooks/useWindowSize';
import {
  marketplaceAddDirectRequestAction,
  marketplaceCatalogRequestAction,
  marketplaceCloseDetailsAction,
  marketplaceDropReplacedCartAction,
  marketplaceOpenDetailsAction,
  marketplaceProductRequestAction,
  marketplaceRemoveFromCartAction,
  marketplaceRestoreCartAction,
  marketplaceSetCartOpenAction,
  marketplaceSetFiltersAction,
  marketplaceSetSupplierFilterAction,
  marketplaceSwapCancelAction,
} from '../../store/actions/marketplace';
import { selectGreetingTitle, selectHasPicksToReturnTo } from '../../store/selectors/derived';
import { selectIsCountryChanging, selectIsCountryGated, selectIsEmbed } from '../../store/selectors/init';
import {
  selectCartCount,
  selectCheckingProductId,
  selectHasNextPage,
  selectIsCartOpen,
  selectIsCatalogResultStale,
  selectIsMarketplaceLoading,
  selectIsMarketplaceLoadingMore,
  selectIsSupplierFilterOn,
  selectMarketplaceCart,
  selectMarketplaceError,
  selectMarketplaceItems,
  selectOpenProductId,
  selectReplacedCart,
  selectResultCatalogContext,
  selectResultFilterName,
  selectSupplierLock,
  selectVisibleMarketplaceItems,
} from '../../store/selectors/marketplace';
import { IMarketplaceCatalogItem } from '../../types/marketplace';
import {
  canAffordCredits,
  doesCartFit,
  getCartLineForProduct,
  hasSpentCredits,
  isProductInCart,
} from '../../util/credits';
import { getConvertedPathname } from '../../util/helpers';
import { hasFilterValues, isOtherSupplier } from '../../util/marketplace';
import { MarketplaceCartDrawer } from './components/MarketplaceCartDrawer';
import { MarketplaceDetailsDrawer } from './components/MarketplaceDetailsDrawer';
import { MarketplaceEmptyState } from './components/MarketplaceEmptyState';
import { MarketplaceItemCard } from './components/MarketplaceItemCard';
import { MarketplaceToolbar } from './components/MarketplaceToolbar';
import { SupplierLockBanner } from './components/SupplierLockBanner';

const getProductKey = (item: IMarketplaceCatalogItem) => item.product_id;

const introTextSx: SxProps<Theme> = { color: ({ palette }) => darken(palette.text.secondary, 0.15), my: 0 };

const introShippingIconSx: SxProps<Theme> = {
  fontSize: 18,
  color: 'info.main',
  verticalAlign: 'text-bottom',
  mr: 0.5,
};

const introShippingTailSx: SxProps<Theme> = { color: 'text.primary', fontWeight: 'medium' };

const titleBackButtonSx: SxProps<Theme> = { color: 'text.primary', ml: -1 };

const picksLinkSx: SxProps<Theme> = {
  fontSize: 14,
  fontWeight: 'medium',
  '&:hover': { backgroundColor: ({ palette }) => alpha(palette.primary.main, 0.08) },
};

const selectionSectionSx: SxProps<Theme> = { mt: 1.5 };

const loadingSx: SxProps<Theme> = { display: 'flex', justifyContent: 'center', py: 8 };

const loadingMoreSx: SxProps<Theme> = { display: 'flex', justifyContent: 'center', mt: 3 };

const loadMoreErrorSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexWrap: 'wrap',
  gap: 1,
  mt: 3,
  color: 'text.secondary',
};

const gridWrapperSx: SxProps<Theme> = { paddingBottom: 7 };

const lockedGridSx: SxProps<Theme> = { ...gridWrapperSx, pointerEvents: 'none', opacity: LOCKED_OPACITY };

const selectorSx: SxProps<Theme> = { ml: 'auto' };

const Marketplace = () => {
  const theme = useTheme();
  const { mobile } = useWindowSize();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const history = useHistory();
  const { pathname } = useLocation();

  const loadedItems = useSelector(selectMarketplaceItems);
  const items = useSelector(selectVisibleMarketplaceItems);
  const isSupplierFilterOn = useSelector(selectIsSupplierFilterOn);
  const resultContext = useSelector(selectResultCatalogContext);
  const resultFilterName = useSelector(selectResultFilterName);
  const isResultStale = useSelector(selectIsCatalogResultStale);
  const cart = useSelector(selectMarketplaceCart);
  const cartCount = useSelector(selectCartCount);
  const isLoading = useSelector(selectIsMarketplaceLoading);
  const isLoadingMore = useSelector(selectIsMarketplaceLoadingMore);
  const error = useSelector(selectMarketplaceError);
  const hasNextPage = useSelector(selectHasNextPage);
  const hasPicksToReturnTo = useSelector(selectHasPicksToReturnTo);
  const isEmbed = useSelector(selectIsEmbed);
  const isCountryGated = useSelector(selectIsCountryGated);
  const isCountryChanging = useSelector(selectIsCountryChanging);
  const checkingProductId = useSelector(selectCheckingProductId);
  const openProductId = useSelector(selectOpenProductId);
  const isCartOpen = useSelector(selectIsCartOpen);
  const supplierLock = useSelector(selectSupplierLock);
  const replacedCart = useSelector(selectReplacedCart);

  // Remounts the snackbar per replace so a second replace gets a full timeout.
  const [replaceKey, setReplaceKey] = useState(0);

  useEffect(() => {
    if (replacedCart) {
      setReplaceKey((key) => key + 1);
    }
  }, [replacedCart]);

  // The flow steps are keyed by the bare route, as every other step container passes it.
  const greetingTitleSelector = useMemo(() => selectGreetingTitle(getConvertedPathname(pathname)), [pathname]);
  const greetingTitle = useSelector(greetingTitleSelector);

  const selectionSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isEmbed) {
      dispatch(marketplaceCatalogRequestAction());
    }
  }, [dispatch, isEmbed]);

  const handleFetchNextPage = useCallback(() => {
    dispatch(marketplaceCatalogRequestAction({ isLoadMore: true }));
  }, [dispatch]);

  const isCatalogEmpty = items.length === 0;
  // Loaded items exist but none ship from the locked supplier: reads as a narrowed search once
  // the catalog is exhausted; before that the next page may still carry some.
  const isEmptiedBySupplierFilter = isSupplierFilterOn && isCatalogEmpty && loadedItems.length > 0;
  const isAwaitingFilteredPage = isEmptiedBySupplierFilter && hasNextPage && !error;
  // A cursor still pending after a failure means the next page, not the list on screen, failed.
  const hasLoadMoreFailed = !isCatalogEmpty && hasNextPage && !!error;

  // Paging stops on a failure: a viewport past the threshold would otherwise re-request the page
  // on every render. Retry is the way back in.
  useInfiniteScroll({
    hasNextPage: hasNextPage && !error,
    isFetchingNextPage: isLoadingMore,
    fetchNextPage: handleFetchNextPage,
    threshold: MARKETPLACE_INFINITE_SCROLL_THRESHOLD,
    recheckKey: items.length,
  });

  const handleRetry = useCallback(() => dispatch(marketplaceCatalogRequestAction()), [dispatch]);

  const handleOpenCart = useCallback(() => dispatch(marketplaceSetCartOpenAction(true)), [dispatch]);

  const handleCloseCart = useCallback(() => {
    dispatch(marketplaceSetCartOpenAction(false));
    dispatch(marketplaceSwapCancelAction());
  }, [dispatch]);

  const handleRemovePick = useCallback(
    (productId: string) => {
      const line = getCartLineForProduct(productId, cart);

      if (line) {
        dispatch(marketplaceRemoveFromCartAction(line.variant_id));
      }
    },
    [dispatch, cart],
  );

  const handleOpenDetails = useCallback(
    (productId: string) => {
      dispatch(marketplaceProductRequestAction(productId));
      dispatch(marketplaceOpenDetailsAction(productId));
    },
    [dispatch],
  );

  const handleCloseDetails = useCallback(() => dispatch(marketplaceCloseDetailsAction()), [dispatch]);

  const picksReplacedText = useMemo(() => getPicksReplacedText(supplierLock?.name), [supplierLock?.name]);

  const handleUndoReplace = useCallback(() => dispatch(marketplaceRestoreCartAction()), [dispatch]);

  const handleCloseSnackbar = useCallback(() => dispatch(marketplaceDropReplacedCartAction()), [dispatch]);

  // The snackbar's timer dies with the page, so the Undo offer must too.
  useEffect(
    () => () => {
      dispatch(marketplaceDropReplacedCartAction());
    },
    [dispatch],
  );

  // The saga decides once details resolve whether a single-variant product skips the sidebar.
  const handleAddDirect = useCallback(
    (productId: string) => dispatch(marketplaceAddDirectRequestAction(productId)),
    [dispatch],
  );

  const handleClearFilters = useCallback(() => {
    dispatch(marketplaceSetFiltersAction(CLEARED_MARKETPLACE_FILTERS));
    dispatch(marketplaceSetSupplierFilterAction(false));
  }, [dispatch]);

  // The spent-budget verdict blames the picked values, so only those go; Fits budget stays on.
  const handleClearFiltersKeepBudget = useCallback(() => {
    dispatch(marketplaceSetFiltersAction(CLEARED_MARKETPLACE_FILTER_VALUES));
  }, [dispatch]);

  const handleShowAllItems = useCallback(() => {
    dispatch(marketplaceSetFiltersAction({ fitsBudget: false }));
  }, [dispatch]);

  const handleClearSearch = useCallback(() => {
    dispatch(marketplaceSetFiltersAction({ query: '' }));
  }, [dispatch]);

  const handleContinue = useCallback(() => {
    dispatch(marketplaceSetCartOpenAction(false));
    history.push(ROUTE_NAMES.address);
  }, [dispatch, history]);

  const handleBackToPicks = useCallback(() => {
    history.push(ROUTE_NAMES.pyg);
  }, [history]);

  const renderCardItem = useCallback(
    (item: IMarketplaceCatalogItem) => (
      <MarketplaceItemCard
        item={item}
        inCart={isProductInCart(item.product_id, cart)}
        canAdd={canAffordCredits(item.creditPrice, cart)}
        hasPicks={cart.length > 0}
        isChecking={checkingProductId === item.product_id}
        isOtherSupplier={isOtherSupplier(item.supplierCode, supplierLock)}
        onOpen={handleOpenDetails}
        onRemove={handleRemovePick}
        onAdd={handleAddDirect}
        onReplace={handleOpenDetails}
      />
    ),
    [cart, supplierLock, handleRemovePick, handleOpenDetails, handleAddDirect, checkingProductId],
  );

  const isContinueDisabled = cartCount === 0 || !doesCartFit(cart);
  // Stale covers the debounce gap before the request starts.
  const isCatalogLoaderShown = isLoading || isResultStale || isAwaitingFilteredPage;
  const isSelectorShown = isCountryGated && !hasPicksToReturnTo;

  const continueFooterButton = useMemo(
    () => (
      <Button
        variant="contained"
        color="primary"
        onClick={handleContinue}
        disabled={isContinueDisabled || isCountryChanging}
        endIcon={<ChevronRightIcon sx={{ fontSize: 18 }} />}
        fullWidth={isMobileViewport}
        size="medium"
      >
        {CONTINUE_TO_SHIPPING_LABEL}
      </Button>
    ),
    [handleContinue, isContinueDisabled, isCountryChanging, isMobileViewport],
  );

  const footerHelperText = useMemo(() => {
    const variant = isMobileViewport ? 'body2' : 'body1';

    if (cartCount > 0) {
      return (
        <Button variant="text" color="primary" onClick={handleOpenCart} disabled={isCountryChanging} sx={picksLinkSx}>
          {getItemsAddedText(cartCount)}
        </Button>
      );
    }

    return (
      <Typography variant={variant} color="text.secondary">
        {ADD_ITEM_TO_CONTINUE_TEXT}
      </Typography>
    );
  }, [cartCount, isMobileViewport, handleOpenCart, isCountryChanging]);

  // Only when there is a curated list to return to. The Footer hides it on mobile, where the
  // sticky toolbar already carries a back arrow.
  const backFooterButton = useMemo(() => {
    if (!hasPicksToReturnTo) {
      return undefined;
    }

    return (
      <Button variant="outlined" color="primary" onClick={handleBackToPicks} startIcon={<ChevronLeftIcon />}>
        Back
      </Button>
    );
  }, [hasPicksToReturnTo, handleBackToPicks]);

  const footerConfig = useMemo(
    () => ({
      backButton: backFooterButton,
      helperText: footerHelperText,
      submitButton: continueFooterButton,
    }),
    [backFooterButton, footerHelperText, continueFooterButton],
  );

  useFooter(footerConfig, selectionSectionRef);

  const scrollToTop = useCallback((behavior: ScrollBehavior) => window.scrollTo({ top: 0, behavior }), []);

  const fabConfig = useMemo(
    () => ({
      up: { label: BACK_TO_TOP_LABEL, onActivate: scrollToTop },
      desktopFabEnabled: true,
    }),
    [scrollToTop],
  );

  useFab(fabConfig);

  // Title owns the page's vertical rhythm; the row takes it over so the arrow centres on the text.
  const titleRowSx: SxProps<Theme> = useMemo(
    () => ({
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      mt: mobile ? 3 : 4,
      mb: 2,
    }),
    [mobile],
  );

  const catalogContent = useMemo(() => {
    if (isCatalogLoaderShown) {
      return (
        <Box role="status" aria-label="Loading items" sx={loadingSx}>
          <CircularProgress />
        </Box>
      );
    }

    if (isCatalogEmpty) {
      return (
        <MarketplaceEmptyState
          error={error}
          hasSpentCredits={!isEmptiedBySupplierFilter && hasSpentCredits(resultContext.budget)}
          filterName={resultFilterName}
          hasFilters={hasFilterValues(resultContext) || isEmptiedBySupplierFilter}
          query={resultContext.query}
          hasPicksToReturnTo={hasPicksToReturnTo}
          onRetry={handleRetry}
          onOpenCart={handleOpenCart}
          onShowAllItems={handleShowAllItems}
          onClearFilters={handleClearFilters}
          onClearFiltersKeepBudget={handleClearFiltersKeepBudget}
          onClearSearch={handleClearSearch}
          onBackToPicks={handleBackToPicks}
        />
      );
    }

    return (
      <Box sx={isCountryChanging ? lockedGridSx : gridWrapperSx} aria-busy={isCountryChanging}>
        <GridView
          items={items}
          getItemKey={getProductKey}
          renderItem={renderCardItem}
          columns={MARKETPLACE_GRID_COLUMNS}
          gap={MARKETPLACE_GRID_GAP}
        />
        {isLoadingMore && (
          <Box role="status" aria-label="Loading more items" sx={loadingMoreSx}>
            <CircularProgress size={24} />
          </Box>
        )}
        {hasLoadMoreFailed && (
          <Box role="alert" sx={loadMoreErrorSx}>
            <Typography variant="body2" color="inherit">
              {LOAD_MORE_FAILED_TEXT}
            </Typography>
            <Button variant="text" color="primary" size="small" onClick={handleFetchNextPage}>
              {RETRY_LABEL}
            </Button>
          </Box>
        )}
      </Box>
    );
  }, [
    isCatalogLoaderShown,
    isCatalogEmpty,
    isEmptiedBySupplierFilter,
    error,
    resultContext,
    resultFilterName,
    hasPicksToReturnTo,
    handleRetry,
    handleOpenCart,
    handleShowAllItems,
    handleClearFilters,
    handleClearFiltersKeepBudget,
    handleClearSearch,
    handleBackToPicks,
    items,
    renderCardItem,
    isLoadingMore,
    hasLoadMoreFailed,
    handleFetchNextPage,
    isCountryChanging,
  ]);

  return (
    <AppWrapper>
      <Box sx={titleRowSx}>
        {hasPicksToReturnTo && (
          <IconButton onClick={handleBackToPicks} aria-label={BACK_ARIA_LABEL} sx={titleBackButtonSx}>
            <ArrowBackIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}
        <Title sx={{ my: 0 }}>{greetingTitle || BROWSE_MARKETPLACE_LABEL}</Title>
        {isSelectorShown && !isMobileViewport && <ShippingToSelector sx={selectorSx} />}
      </Box>
      {isSelectorShown && isMobileViewport && <ShippingToSelector />}

      <Subtitle variant="body2" sx={introTextSx}>
        {getBudgetIntroText(MARKETPLACE_TOTAL_CREDITS)} {MARKETPLACE_SHIPPING_INTRO_LEAD}{' '}
        <LocalShippingOutlinedIcon aria-hidden sx={introShippingIconSx} />
        <Box component="span" sx={introShippingTailSx}>
          {MARKETPLACE_SHIPPING_INTRO_TAIL}
        </Box>
      </Subtitle>

      <MarketplaceToolbar onOpenCart={handleOpenCart} isBackShown={hasPicksToReturnTo} onBack={handleBackToPicks} />

      <SupplierLockBanner lock={supplierLock} />

      <Box ref={selectionSectionRef} sx={selectionSectionSx}>
        {catalogContent}
      </Box>

      <MarketplaceDetailsDrawer productId={openProductId} onClose={handleCloseDetails} />
      <MarketplaceCartDrawer open={isCartOpen} onClose={handleCloseCart} onContinue={handleContinue} />
      <UndoSnackbar
        key={replaceKey}
        open={!!replacedCart}
        message={picksReplacedText}
        onUndo={handleUndoReplace}
        onClose={handleCloseSnackbar}
      />
    </AppWrapper>
  );
};

export default Marketplace;
